import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';
import { RefreshCw, MessageSquare, Mail, AlertTriangle, CheckCircle2, Clock, XCircle, ExternalLink } from 'lucide-react';

const STATUS_STYLES = {
  delivered: { color: 'text-green-700 dark:text-green-400', bg: 'bg-green-100 dark:bg-green-500/15', icon: CheckCircle2 },
  sent: { color: 'text-blue-700 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-500/15', icon: Clock },
  queued: { color: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-500/15', icon: Clock },
  undelivered: { color: 'text-red-700 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-500/15', icon: XCircle },
  failed: { color: 'text-red-700 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-500/15', icon: XCircle },
};

const formatDT = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_STYLES[(status || '').toLowerCase()] || { color: 'text-foreground', bg: 'bg-secondary', icon: Clock };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${cfg.color} ${cfg.bg}`} data-testid={`status-${status}`}>
      <Icon size={11} />
      {status}
    </span>
  );
};

const NotificationsHealthPanel = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('sms'); // sms | email
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchHealth = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await api.get('/api/admin/notifications/health?limit=100');
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load notification health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  if (loading) {
    return (
      <div className="card p-8 text-center text-muted-foreground" data-testid="notif-health-loading">
        Loading notification health…
      </div>
    );
  }

  if (!data) return null;

  const smsItems = (data.sms?.items || []).filter(s => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'failed') return ['undelivered', 'failed'].includes((s.status || '').toLowerCase());
    return (s.status || '').toLowerCase() === statusFilter;
  });

  const emailItems = (data.email?.items || []).filter(e => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'failed') return !e.success;
    if (statusFilter === 'delivered') return e.success;
    return true;
  });

  const smsSummary = data.sms?.summary || {};
  const emailSummary = data.email?.summary || {};

  // Surface the toll-free verification problem in plain English
  const has30032 = (data.sms?.items || []).some(s => String(s.error_code) === '30032');

  return (
    <div className="space-y-6" data-testid="notif-health-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-serif">Notifications Health</h2>
          <p className="text-sm text-muted-foreground">Live delivery status for SMS & Email — pulled from Twilio & internal logs.</p>
        </div>
        <button
          onClick={fetchHealth}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-3 py-2 border border-border text-xs uppercase tracking-wide hover:bg-accent disabled:opacity-50"
          data-testid="notif-health-refresh"
        >
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Critical banner for 30032 */}
      {has30032 && (
        <div className="border-l-4 border-red-500 bg-red-50 dark:bg-red-500/10 p-4" data-testid="notif-health-30032-banner">
          <div className="flex gap-3">
            <AlertTriangle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-red-800 dark:text-red-300">SMS delivery is blocked — Toll-Free Verification required</p>
              <p className="text-red-700 dark:text-red-400 mt-1">
                Your toll-free number <strong>{data.sms?.from_number || '—'}</strong> is hitting Twilio error <strong>30032</strong>. US carriers are silently dropping every SMS. Even though the API accepts the message, your crew never receives it. You&apos;re still being charged for failed attempts.
              </p>
              <a
                href="https://console.twilio.com/us1/develop/sms/regulatory-compliance/tollfree-verifications"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-red-800 dark:text-red-300 hover:underline font-medium"
                data-testid="notif-health-twilio-link"
              >
                Submit Toll-Free Verification in Twilio Console <ExternalLink size={12} />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard
          icon={MessageSquare}
          label="SMS · Last 100"
          value={smsSummary.total || 0}
          subLabel={`${smsSummary.delivered || 0} delivered`}
          subColor="text-green-600 dark:text-green-400"
        />
        <SummaryCard
          icon={XCircle}
          label="SMS · Failed"
          value={(smsSummary.undelivered || 0) + (smsSummary.failed || 0)}
          subLabel={smsSummary.total ? `${Math.round(((smsSummary.undelivered || 0) + (smsSummary.failed || 0)) / smsSummary.total * 100)}% of sent` : '—'}
          subColor="text-red-600 dark:text-red-400"
          highlight={(smsSummary.undelivered || 0) + (smsSummary.failed || 0) > 0}
        />
        <SummaryCard
          icon={Mail}
          label="Email · Total"
          value={emailSummary.total || 0}
          subLabel={`${emailSummary.success || 0} succeeded`}
          subColor="text-green-600 dark:text-green-400"
        />
        <SummaryCard
          icon={XCircle}
          label="Email · Failed"
          value={emailSummary.failed || 0}
          subLabel={emailSummary.total ? `${Math.round((emailSummary.failed || 0) / emailSummary.total * 100)}% of sent` : '—'}
          subColor={emailSummary.failed > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}
        />
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-border">
        <SubTabButton active={tab === 'sms'} onClick={() => setTab('sms')} testid="notif-health-tab-sms">
          <MessageSquare size={14} /> SMS ({smsItems.length})
        </SubTabButton>
        <SubTabButton active={tab === 'email'} onClick={() => setTab('email')} testid="notif-health-tab-email">
          <Mail size={14} /> Email ({emailItems.length})
        </SubTabButton>
        <div className="ml-auto flex items-center gap-2 pb-2">
          <label className="text-xs text-muted-foreground uppercase tracking-wide">Filter</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-2 py-1 border border-input bg-background text-xs"
            data-testid="notif-health-status-filter"
          >
            <option value="all">All</option>
            {tab === 'sms' ? (
              <>
                <option value="delivered">Delivered</option>
                <option value="sent">Sent</option>
                <option value="queued">Queued</option>
                <option value="undelivered">Undelivered</option>
                <option value="failed">Failed</option>
              </>
            ) : (
              <>
                <option value="delivered">Succeeded</option>
                <option value="failed">Failed</option>
              </>
            )}
          </select>
        </div>
      </div>

      {/* Tables */}
      {tab === 'sms' && (
        <div className="card overflow-x-auto" data-testid="notif-health-sms-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">To</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Error</th>
                <th className="px-3 py-2">Message</th>
                <th className="px-3 py-2 text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {smsItems.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">No SMS matching filter.</td></tr>
              ) : smsItems.map((s) => (
                <tr key={s.sid} className="border-t border-border hover:bg-muted/30" data-testid={`sms-row-${s.sid}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-xs">{formatDT(s.date_sent || s.date_created)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{s.to}</td>
                  <td className="px-3 py-2"><StatusBadge status={s.status} /></td>
                  <td className="px-3 py-2 text-xs">
                    {s.error_code ? (
                      <span className="text-red-700 dark:text-red-400">
                        <strong>{s.error_code}</strong>
                        {s.error_message ? ` · ${s.error_message}` : ''}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground max-w-md truncate" title={s.body}>{s.body}</td>
                  <td className="px-3 py-2 text-right text-xs font-mono">{s.price ? `${s.price} ${s.price_unit || ''}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'email' && (
        <div className="card overflow-x-auto" data-testid="notif-health-email-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">To</th>
                <th className="px-3 py-2">Crew</th>
                <th className="px-3 py-2">Event</th>
                <th className="px-3 py-2">Kind</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {emailItems.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">No email logs matching filter.</td></tr>
              ) : emailItems.map((e, idx) => (
                <tr key={`${e.sent_at}-${idx}`} className="border-t border-border hover:bg-muted/30" data-testid={`email-row-${idx}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-xs">{formatDT(e.sent_at)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{e.to || '—'}</td>
                  <td className="px-3 py-2 text-xs">{e.crew_member_name || '—'}</td>
                  <td className="px-3 py-2 text-xs">{e.event_name || '—'}</td>
                  <td className="px-3 py-2 text-xs"><span className="px-2 py-0.5 bg-muted text-muted-foreground uppercase text-[10px]">{e.kind}</span></td>
                  <td className="px-3 py-2"><StatusBadge status={e.success ? 'delivered' : 'failed'} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ icon: Icon, label, value, subLabel, subColor, highlight }) => (
  <div className={`card p-4 ${highlight ? 'border-red-300 bg-red-50/40' : ''}`}>
    <div className="flex items-center gap-2 mb-1 text-muted-foreground">
      <Icon size={14} />
      <p className="text-xs uppercase tracking-wide">{label}</p>
    </div>
    <p className="text-2xl font-mono">{value}</p>
    <p className={`text-xs ${subColor || 'text-muted-foreground'}`}>{subLabel}</p>
  </div>
);

const SubTabButton = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors ${active ? 'border-b-2 border-black text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
    data-testid={testid}
  >
    {children}
  </button>
);

export default NotificationsHealthPanel;
